/**
 * What a number field in an engine's settings saves when it loses focus.
 *
 * The scan autopilot's and the brain autopilot's forms each carried a private
 * copy of this rule inside a private `<input>`. It is the part of the field
 * with consequences - every value it lets through changes how often an engine
 * runs or how much it may fetch - so it lives here, React-free, where
 * `numberField.test.ts` can pin it down under plain Node.
 *
 * The rule is the one both copies had. What was typed is read as a whole
 * number (`parseInt`, so "20 min" reads as 20); anything that is not a number
 * puts the stored value back rather than saving a guess; a number outside the
 * field's range is clamped to it, the same limits the server enforces, so the
 * field never shows a value the engine is not running on. Nothing is saved
 * when the clamped value is the one already stored.
 */

export interface NumberCommit {
  /** What the field shows after the commit, and what is saved if `changed`. */
  value: number;
  /** True when `value` differs from the stored one and must be saved. */
  changed: boolean;
}

export function commitNumber(text: string, stored: number, min: number, max: number): NumberCommit {
  const n = Number.parseInt(text, 10);
  if (!Number.isFinite(n)) return { value: stored, changed: false };
  const value = Math.min(max, Math.max(min, n));
  return { value, changed: value !== stored };
}
